'use client';

import { Link } from 'react-router-dom';
import {
  Calculator,
  Receipt,
  FolderKanban,
  Users,
  Map,
  BarChart3,
  ArrowUpRight,
} from 'lucide-react';
import { AnimatedSection, StaggerItem } from './AnimatedSection';
import { SpringCard } from './motion';
import { PRODUCT_FRAMES } from '@/lib/productFrames';

/**
 * Feature bento v3 — cards carry the poster frames of the choreographed films
 * (films/*.html → public/product-frames/videos/), the same art direction as
 * the hero. Playable versions live on the feature pages and the product tour.
 * Cards without a film use code-native data compositions (no full-app
 * screenshots): the command-center tile and the channel-attribution bars.
 * Every figure on these cards is illustrative — not seeded demo data and not
 * customer results — and each card says so.
 */
const FEATURES = [
  {
    icon: Calculator,
    title: 'Satellite Estimator Engine',
    description:
      'Satellite-powered roof measurements, multi-tier pricing, and automatic waste calculations.',
    link: '/features/estimating',
    image: PRODUCT_FRAMES.instant.still,
    imageAlt:
      'Illustration: a roof outline on a dark map beside good, better, and best prices for one address',
    stat: '2.5x',
    statLabel: 'faster estimates',
    span: 'lg:col-span-2 lg:row-span-2',
    tall: true,
  },
  {
    icon: Receipt,
    title: 'Invoicing & Payments',
    description: 'Branded invoices, online payments, and automated reminders.',
    link: '/features/invoicing',
    image: PRODUCT_FRAMES.invoices.still,
    imageAlt: 'Illustration: an invoice stamped paid with a $0 balance due',
    span: '',
  },
  {
    icon: FolderKanban,
    title: 'Project Management',
    description: 'Track every job from lead to completion with stages and team assignments.',
    link: '/features/project-management',
    visual: CommandCenterVisual,
    span: '',
  },
  {
    icon: Users,
    title: 'Customer CRM',
    description: 'Unified contact hub with lifecycle tracking and smart follow-ups.',
    link: '/features/crm',
    image: PRODUCT_FRAMES.pipeline.still,
    imageAlt: 'Illustration: a four-stage sales pipeline board with one deal landed in Won',
    span: '',
  },
  {
    icon: Map,
    title: 'Territory & Canvassing',
    description: 'Zip-based territories, route optimization, and offline door-knocking mode.',
    link: '/features/storm-canvassing',
    image: PRODUCT_FRAMES.canvassing.still,
    imageAlt: 'Illustration: two canvassing territories on a dark map, one dotted with knock pins',
    span: 'lg:col-span-2',
  },
  {
    icon: BarChart3,
    title: 'Marketing Automation & ROI',
    description:
      'Storm and retail nurture journeys with cost-per-lead and channel attribution in one view.',
    link: '/features/marketing-dashboard',
    visual: ChannelBarsVisual,
    span: '',
  },
];

/** Illustrative command-center figures as a 2×2 grid — the story the
 *  dashboard frame tells, without a full-app screenshot. A grid, not four
 *  stacked rows: the rows needed ~194px and the 16:7 media slot is 136–173px,
 *  so the first and last rows were clipped at every width. */
function CommandCenterVisual() {
  const cells = [
    { k: 'Needs attention', v: '3', tone: 'text-sky-400' },
    { k: 'Crews on jobs', v: '5', tone: 'text-white' },
    { k: "Today's focus", v: '4 jobs', tone: 'text-white' },
    { k: 'Awaiting sign-off', v: '2', tone: 'text-emerald-400' },
  ];
  return (
    <div
      className="absolute inset-0 bg-[#090D16] px-6 pt-4 pb-6 flex flex-col justify-center gap-2.5"
      role="img"
      aria-label="Illustrative command center: 3 items need attention, 5 crews on jobs, 4 jobs in today's focus, 2 quotes awaiting sign-off"
    >
      <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
        Command center · illustrative figures
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        {cells.map(({ k, v, tone }) => (
          <div key={k} className="min-w-0">
            <p className={`text-base font-bold leading-5 tabular-nums ${tone}`}>{v}</p>
            <p className="truncate text-xs leading-4 text-slate-400">{k}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Channel attribution mini-bars (illustrative figures) — the marketing
 *  dashboard's actual subject, drawn rather than screenshotted. */
function ChannelBarsVisual() {
  const bars = [
    { k: 'Google Ads', pct: 38 },
    { k: 'Meta', pct: 27 },
    { k: 'Referral', pct: 19 },
    { k: 'Direct', pct: 16 },
  ];
  return (
    <div
      className="absolute inset-0 bg-[#090D16] px-6 pt-4 pb-6 flex flex-col justify-center gap-2.5"
      role="img"
      aria-label="Illustrative channel attribution: Google Ads 38 percent, Meta 27, Referral 19, Direct 16"
    >
      <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">
        Leads by channel · illustrative figures
      </p>
      {bars.map(({ k, pct }) => (
        <div key={k} className="flex items-center gap-3">
          <span className="w-20 text-xs text-slate-300 shrink-0">{k}</span>
          <div className="h-2 flex-1 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full bg-sky-400/85" style={{ width: `${pct}%` }} />
          </div>
          <span className="w-9 text-right text-xs font-semibold text-white/85 tabular-nums">
            {pct}%
          </span>
        </div>
      ))}
    </div>
  );
}

/** Link product previews and capability summaries to their feature pages. */
export function FeatureBento() {
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
          <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/90 border border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-300 mb-5">
            Platform
          </p>
          <h2 className="display-headline text-3xl sm:text-4xl lg:text-5xl mb-5">
            One platform. <span className="headline-gradient">Every phase of the job.</span>
          </h2>
          <p className="text-lg text-white/55">
            Rooflyzer replaces the patchwork of tools you use today with a single operating system
            built for how roofers actually work.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(({ visual: Visual, ...feature }) => (
            <StaggerItem
              key={feature.title}
              className={`group relative rounded-3xl ${feature.span}`}
            >
              <SpringCard className="h-full">
                <Link
                  to={feature.link}
                  className="glass-card block h-full rounded-3xl overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-500"
                >
                  {/* Product visual — film poster, data composition, or crop.
                    max-w-full: an aspect-ratio box carries its min-height into
                    a min-width (168px × 16/7 = 384px), which is wider than a
                    phone card; uncapped, the card cropped the slot's right edge. */}
                  <div
                    className={`relative max-w-full overflow-hidden ${
                      feature.tall
                        ? 'aspect-[16/11] lg:aspect-auto lg:h-[58%] min-h-[260px]'
                        : // min-h: at 1024px three columns make the 16:7 slot
                          // only 136px, which clipped the channel bars' last row.
                          'aspect-[16/7] min-h-[168px]'
                    }`}
                  >
                    {Visual ? (
                      <Visual />
                    ) : (
                      <>
                        {/* Driven by the card's `group` hover: the gradient overlay
                          sits above the image, so a hover on the image itself
                          never fired. */}
                        <img
                          src={feature.image}
                          alt={feature.imageAlt}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover object-top motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover:scale-[1.045]"
                        />
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/25 to-transparent"
                        />
                        {/* Visible label at the point of presentation: the posters
                          are code-drawn films, not app captures. The alt text
                          already opens with "Illustration:", so screen readers
                          skip this badge rather than hear it twice. */}
                        <span
                          aria-hidden
                          data-illustration-badge
                          className="pointer-events-none absolute right-3 top-3 z-10 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.12em] text-white/75 backdrop-blur"
                        >
                          Illustration
                        </span>
                      </>
                    )}
                  </div>

                  <div className="relative p-6 sm:p-7 -mt-8">
                    <div className="flex items-start justify-between mb-4">
                      <span className="icon-chip">
                        <feature.icon className="size-5" />
                      </span>
                      <span className="opacity-0 -translate-x-1 group-hover:opacity-100 motion-safe:group-hover:translate-x-0 motion-safe:transition-[opacity,transform] duration-500 p-2 rounded-full bg-white/[0.06] text-white/80 hover:text-white">
                        <ArrowUpRight className="size-4" />
                      </span>
                    </div>

                    <h3 className="text-xl font-display font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>

                    {feature.stat && (
                      <div className="mt-5 pt-5 border-t border-white/[0.06]">
                        <p className="text-3xl font-display font-bold headline-gradient inline-block">
                          {feature.stat}
                        </p>
                        <p className="text-xs text-white/40 uppercase tracking-wider mt-1">
                          {feature.statLabel}
                        </p>
                      </div>
                    )}
                  </div>
                </Link>
              </SpringCard>
            </StaggerItem>
          ))}
        </div>
      </div>
    </section>
  );
}
